import { Button } from '@mui/material'

import { BottomSafeArea, Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FileUploadInfo } from '@/components/pages/ConsoleCollectionCreate/template/CollectionCreateForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import {
  CollectionEditForm,
  CollectionEditFormProps,
} from './CollectionEditForm'

export * from './fragments'

export type State = {
  artistId: string
  progress: number
  updating: boolean
  loading: boolean
  collection: CollectionEditFormProps['collection']
}

type FixedButtonCellProps = {
  loading: boolean
  disabled: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: CollectionEditFormProps['onSubmit']
  onDrop: FileUploadInfo['onDrop']
}

export const ConsoleCollectionEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onDrop,
}) => {
  const { routerNames, routerPages, generatePath } = useRouter()
  const { t } = useTranslation('consoleCollectionEdit')

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCollectionEdit} />
      <ConsoleBaseLayout
        margin
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleCollectionDetail, {
            collectionId: state.collection?.id ?? '#',
          }),
        }}
        customNavigation={
          <FixedButtonCell
            loading={state.updating || !!state.progress}
            disabled={state.loading}
          />
        }
      >
        <CollectionEditForm
          artistId={state.artistId}
          fileUploadInfo={{ progress: state.progress, onDrop }}
          collection={state.collection}
          onSubmit={onSubmit}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const FixedButtonCell: Cell<FixedButtonCellProps> = ({ loading, disabled }) => {
  const { t } = useTranslation('consoleCollectionEdit')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame
        sx={{
          py: 4,
          display: 'flex',
          bgcolor: 'grey.50',
          justifyContent: 'center',
        }}
      >
        <Button
          form="collection-edit-form"
          type="submit"
          size="large"
          loading={loading}
          disabled={disabled || loading}
          sx={{ width: 244 }}
        >
          {t('save')}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}
